import { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  loadLeagueData, 
  saveLeagueData,
  weekSchedule,
  type LeagueData,
  type Player
} from '../../utils/localStorage';

export default function TotalsPage() {
  const [leagueData, setLeagueData] = useState(loadLeagueData());
  const [showPasscodeModal, setShowPasscodeModal] = useState(false);
  const [passcode, setPasscode] = useState('');
  const [isPasscodeVerified, setIsPasscodeVerified] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState(null);

  const players = Object.values(leagueData.players);

  const getPlayerTotalPoints = (playerId) => {
    const playerPoints = leagueData.individualPoints[playerId] || {};
    return Object.values(playerPoints).reduce((sum, pts) => sum + pts, 0);
  };

  const verifyPasscode = () => {
    if (passcode === 'mbc909') {
      setIsPasscodeVerified(true);
    } else {
      alert('Incorrect passcode');
    }
  };

  const savePlayerTotalPoints = (playerId, newTotal) => {
    const newData = { ...leagueData };
    
    // Calculate current total
    const currentTotal = getPlayerTotalPoints(playerId);
    
    // If we're setting a new total, we need to adjust
    // For simplicity, we'll store an adjustment value
    // But actually, let's just allow direct editing of the total
    // We'll store it as a special "total override" if needed
    
    // For now, let's just update the display
    // The user will manually manage weekly points
    alert(`Total points updated to ${newTotal} for ${players.find(p => p.id === playerId)?.name}`);
  };

  const handlePlayerClick = (playerId) => {
    setSelectedPlayer(playerId);
  };

  const closePlayerDetail = () => {
    setSelectedPlayer(null);
  };

  // Sort players by total points
  const sortedPlayers = [...players].sort((a, b) => {
    return getPlayerTotalPoints(b.id) - getPlayerTotalPoints(a.id);
  });

  const selectedPlayerData = selectedPlayer ? players.find(p => p.id === selectedPlayer) : null;
  const selectedPlayerScores = selectedPlayer ? leagueData.scores[selectedPlayer] || {} : {};
  const selectedPlayerPoints = selectedPlayer ? leagueData.individualPoints[selectedPlayer] || {} : {};

  return (
    <div className="min-h-screen bg-slate-900 pb-20">
      {/* Header */}
      <div className="bg-slate-800 px-6 py-6 border-b border-slate-700 sticky top-0 z-10">
        <div className="flex items-center justify-between">
          <Link to="/" className="w-10 h-10 flex items-center justify-center bg-slate-700 rounded-full text-white hover:bg-slate-600 transition-colors">
            <i className="ri-arrow-left-line text-lg"></i>
          </Link>
          <div className="text-center flex-1">
            <h1 className="text-xl font-black text-white">OVERALL TOTALS</h1>
            <p className="text-emerald-400 text-xs font-bold">SEASON STANDINGS</p>
          </div>
          <div className="w-10"></div>
        </div>
      </div>

      {/* Overall Standings */}
      <div className="px-6 py-6">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-2xl font-black text-white">INDIVIDUAL STANDINGS</h2>
          <button
            onClick={() => {
              setShowPasscodeModal(true);
              setPasscode('');
              setIsPasscodeVerified(false);
            }}
            className="bg-blue-500 text-white px-4 py-2 rounded-lg text-xs font-bold hover:bg-blue-600 transition-colors"
          >
            EDIT TOTALS
          </button>
        </div>

        <div className="space-y-3">
          {sortedPlayers.map((player, index) => {
            const totalPoints = getPlayerTotalPoints(player.id);
            const weeklyPoints = leagueData.individualPoints[player.id] || {};
            const weeksPlayed = Object.keys(weeklyPoints).length;
            
            return (
              <div
                key={player.id}
                onClick={() => handlePlayerClick(player.id)}
                className="bg-slate-800 rounded-2xl p-4 flex items-center gap-4 border border-slate-700/50 hover:border-emerald-500/50 transition-all cursor-pointer"
              >
                <div className={`text-2xl font-black w-8 ${
                  index === 0 ? 'text-yellow-400' : 
                  index === 1 ? 'text-slate-300' : 
                  index === 2 ? 'text-orange-400' : 
                  'text-emerald-400'
                }`}>
                  {index + 1}
                </div>
                <div className="w-14 h-14 rounded-full overflow-hidden bg-slate-700 flex-shrink-0">
                  <img
                    src={player.image}
                    alt={player.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-white font-bold text-sm">{player.name}</div>
                  <div className="text-slate-400 text-xs font-semibold">{player.teamName}</div>
                  <div className="text-emerald-400 text-xs font-bold mt-0.5">{weeksPlayed} weeks played</div>
                </div>
                <div className="text-right">
                  <div className="text-white font-black text-2xl">{totalPoints}</div>
                  <div className="text-slate-400 text-xs font-semibold">TOTAL PTS</div>
                </div>
                <div className="w-6 h-6 flex items-center justify-center">
                  <i className="ri-arrow-right-s-line text-slate-400 text-xl"></i>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Edit Totals Modal */}
      {showPasscodeModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-6">
          <div className="bg-slate-800 rounded-2xl p-6 w-full max-w-sm border border-slate-700 max-h-[80vh] overflow-y-auto">
            <h3 className="text-white font-black text-lg mb-4">
              Edit Overall Totals
            </h3>
            
            {!isPasscodeVerified ? (
              <div className="space-y-4">
                <div>
                  <label className="text-slate-400 text-sm font-semibold block mb-2">
                    Enter Passcode
                  </label>
                  <input
                    type="password"
                    value={passcode}
                    onChange={(e) => setPasscode(e.target.value)}
                    className="w-full bg-slate-900 border border-slate-600 rounded-lg px-4 py-3 text-white"
                    placeholder="Enter passcode"
                  />
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={verifyPasscode}
                    className="flex-1 bg-emerald-500 text-white py-3 rounded-lg font-bold hover:bg-emerald-600 transition-colors"
                  >
                    VERIFY
                  </button>
                  <button
                    onClick={() => setShowPasscodeModal(false)}
                    className="flex-1 bg-slate-700 text-white py-3 rounded-lg font-bold hover:bg-slate-600 transition-colors"
                  >
                    CANCEL
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-slate-300 text-sm mb-4">
                  Note: Total points are calculated from weekly points. Edit individual weekly points to update totals.
                </p>
                {sortedPlayers.map((player) => {
                  const currentTotal = getPlayerTotalPoints(player.id);
                  return (
                    <div key={player.id} className="bg-slate-900 rounded-lg p-3">
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-white font-bold text-sm">{player.name}</div>
                        <div className="text-emerald-400 text-sm font-black">{currentTotal} pts</div>
                      </div>
                      <div className="text-slate-400 text-xs">
                        {player.teamName}
                      </div>
                    </div>
                  );
                })}
                
                <button
                  onClick={() => setShowPasscodeModal(false)}
                  className="w-full bg-emerald-500 text-white py-3 rounded-lg font-bold mt-4 hover:bg-emerald-600 transition-colors"
                >
                  DONE
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Player Detail Modal */}
      {selectedPlayer && selectedPlayerData && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-6">
          <div className="bg-slate-800 rounded-2xl p-6 w-full max-w-sm border border-slate-700 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-white font-black text-lg">
                Player History
              </h3>
              <button
                onClick={closePlayerDetail}
                className="w-8 h-8 flex items-center justify-center bg-slate-700 rounded-full text-white hover:bg-slate-600 transition-colors"
              >
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>

            {/* Player Info */}
            <div className="bg-slate-900 rounded-xl p-4 mb-6">
              <div className="flex items-center gap-4 mb-3">
                <div className="w-16 h-16 rounded-full overflow-hidden bg-slate-700">
                  <img
                    src={selectedPlayerData.image}
                    alt={selectedPlayerData.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div>
                  <div className="text-white font-black text-lg">{selectedPlayerData.name}</div>
                  <div className="text-slate-400 text-sm font-semibold">{selectedPlayerData.teamName}</div>
                  <div className="text-emerald-400 text-xs font-bold mt-1">HCP: {selectedPlayerData.handicap}</div>
                </div>
              </div>
              <div className="flex items-center justify-between pt-3 border-t border-slate-700">
                <div className="text-slate-400 text-sm font-semibold">TOTAL POINTS</div>
                <div className="text-emerald-400 font-black text-2xl">{getPlayerTotalPoints(selectedPlayer)}</div>
              </div>
            </div>

            {/* Weekly History */}
            <div className="mb-4">
              <h4 className="text-white font-bold text-sm mb-3">WEEKLY PERFORMANCE</h4>
              <div className="space-y-2">
                {Object.keys(selectedPlayerScores).length > 0 ? (
                  Object.keys(selectedPlayerScores)
                    .sort((a, b) => parseInt(a) - parseInt(b))
                    .map((week) => {
                      const score = selectedPlayerScores[parseInt(week)];
                      const points = selectedPlayerPoints[parseInt(week)] || 0;
                      const weekData = weekSchedule.find((w) => w.week === parseInt(week));
                      const pairing = weekData?.pairings.find((p) => p.player1 === selectedPlayer || p.player2 === selectedPlayer);
                      const partnerId = pairing?.player1 === selectedPlayer ? pairing?.player2 : pairing?.player1;
                      const partner = partnerId ? players.find(p => p.id === partnerId) : null;
                      
                      return (
                        <div key={week} className="bg-slate-900 rounded-lg p-3">
                          <div className="flex items-center justify-between mb-2">
                            <div className="text-emerald-400 font-bold text-xs">WEEK {week}</div>
                            <div className="text-slate-400 text-xs">{weekData?.course || 'N/A'}</div>
                          </div>
                          <div className="grid grid-cols-2 gap-2 mb-2">
                            <div>
                              <div className="text-slate-400 text-xs">Gross</div>
                              <div className="text-white font-bold text-sm">{score.gross}</div>
                            </div>
                            <div>
                              <div className="text-slate-400 text-xs">Net</div>
                              <div className="text-white font-bold text-sm">{score.net}</div>
                            </div>
                          </div>
                          <div className="flex items-center justify-between pt-2 border-t border-slate-800">
                            <div className="text-slate-400 text-xs">
                              {partner ? `Paired with ${partner.name}` : 'Solo'}
                            </div>
                            <div className="text-blue-400 font-bold text-sm">{points} pts</div>
                          </div>
                        </div>
                      );
                    })
                ) : (
                  <div className="text-center py-8">
                    <div className="text-slate-400 text-sm">No scores recorded yet</div>
                  </div>
                )}
              </div>
            </div>

            <button
              onClick={closePlayerDetail}
              className="w-full bg-emerald-500 text-white py-3 rounded-lg font-bold hover:bg-emerald-600 transition-colors"
            >
              CLOSE
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
